class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this._drawerDuration = 420;
    this._noteAccordionDuration = 350;

    this.addEventListener('click', this.onDrawerClick.bind(this));
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.initNoteAccordion();
    this.setHeaderCartIconAccessibility();
  }

  onDrawerClick(event) {
    if (!this.classList.contains('active') || this.classList.contains('is-closing')) return;
    if (event.target.closest('.drawer__inner')) return;
    this.close();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();

      const headerDrawer = document.querySelector('header-drawer');
      const menuDetails =
        headerDrawer?.querySelector('.menu-drawer-container') || headerDrawer?.querySelector('details');
      const menuIsOpen =
        menuDetails?.hasAttribute('open') && !headerDrawer?.classList.contains('menu-drawer--closing');

      if (menuIsOpen && typeof headerDrawer?.closeMenuDrawer === 'function') {
        const menuSummary = menuDetails.querySelector('summary');
        headerDrawer.closeMenuDrawer(event, menuSummary);
        window.setTimeout(() => this.open(cartLink), this._drawerDuration + 40);
        return;
      }

      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.classList.remove('is-closing');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('animate', 'active');
      });
    });

    const drawerInner = this.querySelector('.drawer__inner');
    const onOpenEnd = (event) => {
      if (event.target !== drawerInner || event.propertyName !== 'transform') return;
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = drawerInner || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    };

    drawerInner?.addEventListener('transitionend', onOpenEnd, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    if (!this.classList.contains('active') || this.classList.contains('is-closing')) return;

    this.classList.add('is-closing');
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);

    const finishClose = () => {
      if (!this.classList.contains('is-closing')) return;
      this.classList.remove('is-closing', 'animate');
      document.body.classList.remove('overflow-hidden');
    };

    const drawerInner = this.querySelector('.drawer__inner');
    let closed = false;
    const completeClose = () => {
      if (closed) return;
      closed = true;
      finishClose();
    };

    drawerInner?.addEventListener(
      'transitionend',
      (event) => {
        if (event.target === drawerInner && event.propertyName === 'transform') completeClose();
      },
      { once: true }
    );

    window.setTimeout(completeClose, this._drawerDuration + 40);
  }

  initNoteAccordion() {
    if (this._noteAccordionInit) return;
    this._noteAccordionInit = true;
    this._noteReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.addEventListener('click', this.onNoteAccordionClick.bind(this));
    this.querySelectorAll('#Details-CartDrawer').forEach((details) => this.syncNoteAccordionState(details));
  }

  syncNoteAccordionState(details) {
    const panel = details.querySelector('.cart-drawer-note-panel');
    const summary = details.querySelector('summary');
    if (!panel || !summary) return;

    const isOpen = details.hasAttribute('open');
    panel.style.gridTemplateRows = isOpen ? '1fr' : '0fr';
    details.classList.toggle('is-open', isOpen);
    summary.setAttribute('role', 'button');
    summary.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }

  onNoteAccordionClick(event) {
    const summary = event.target.closest('#Details-CartDrawer summary');
    if (!summary) return;

    const details = summary.closest('#Details-CartDrawer');
    const panel = details?.querySelector('.cart-drawer-note-panel');
    if (!details || !panel) return;

    event.preventDefault();
    if (details.classList.contains('is-animating')) return;

    if (details.hasAttribute('open')) {
      this.closeNoteAccordion(details, panel);
    } else {
      this.openNoteAccordion(details, panel);
    }
  }

  openNoteAccordion(details, panel) {
    details.setAttribute('open', '');
    details.classList.add('is-open');
    details.querySelector('summary')?.setAttribute('aria-expanded', 'true');

    if (this._noteReducedMotion) {
      panel.style.gridTemplateRows = '1fr';
      return;
    }

    panel.style.gridTemplateRows = '0fr';
    requestAnimationFrame(() => {
      panel.style.gridTemplateRows = '1fr';
    });
  }

  closeNoteAccordion(details, panel) {
    if (this._noteReducedMotion) {
      details.removeAttribute('open');
      details.classList.remove('is-open');
      panel.style.gridTemplateRows = '0fr';
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      return;
    }

    details.classList.add('is-animating');
    panel.style.gridTemplateRows = '0fr';

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      panel.removeEventListener('transitionend', onTransitionEnd);
      clearTimeout(fallbackTimer);
      details.removeAttribute('open');
      details.classList.remove('is-open', 'is-animating');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    };

    const onTransitionEnd = (event) => {
      if (event.target !== panel || event.propertyName !== 'grid-template-rows') return;
      finish();
    };

    panel.addEventListener('transitionend', onTransitionEnd);
    const fallbackTimer = window.setTimeout(finish, this._noteAccordionDuration + 50);
  }

  refreshNoteAccordion() {
    this.querySelectorAll('#Details-CartDrawer').forEach((details) => this.syncNoteAccordionState(details));
  }

  renderContents(parsedState) {
    if (!parsedState?.sections) {
      console.error('Cart drawer: missing sections in add-to-cart response', parsedState);
      return;
    }

    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner?.classList.contains('is-empty')) drawerInner.classList.remove('is-empty');

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections[section.id];
      if (!sectionHtml) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
      sectionElement.innerHTML = innerHTML;
    });

    const errorsEl = document.getElementById('CartDrawer-CartErrors');
    if (errorsEl) errorsEl.textContent = '';

    this.classList.remove('is-empty');
    this.refreshNoteAccordion();

    setTimeout(() => this.open());
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const target =
      (selector && parsed.querySelector(selector)) ||
      parsed.querySelector('.shopify-section') ||
      parsed.body.firstElementChild;

    return target ? target.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  renderCartSections(parsedState) {
    super.renderCartSections(parsedState);
    this.closest('cart-drawer')?.refreshNoteAccordion();
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
